/**
 * `Compare: N of M ▾` — the Budget Overview's picker for which OLDER budget
 * versions appear as columns (owner, 2026-10-05). Presentational: the page owns
 * the state through `useBudgetCompareSelection` and passes it in; this decides
 * nothing about storage or about which versions exist.
 *
 * Its rules → `components/budget/CLAUDE.md` (Manage Budgets), moved there 2026-10-28.
 */
import React, { useId } from 'react';
import Dropdown from '../ui/Dropdown';
import { TOOLBAR_DISCLOSURE_TRIGGER } from '../ui/Toolbar';
import { formatDateShort } from '../../utils/dateFormat';

// `created_at` arrives as SQLite's 'YYYY-MM-DD HH:MM:SS' (a space, no 'T') or a
// PG timestamp; `formatDateShort` only splits on 'T', so take the date part first.
const versionDate = (ts) => formatDateShort(ts ? String(ts).slice(0, 10) : null);

export const BASELINE_TITLE = 'Variance baseline — anchor for Variance Analysis';

export const capReason = (max) =>
  `Only ${max} fit on an A4 landscape print — untick one first`;

// No "(max N)" on the trigger (owner 2026-10-05: "remove (max3) from the
// menu") — the cap is explained by the disabled boxes' reason line instead.
export function compareTriggerLabel(selectedCount, olderCount) {
  return `Compare: ${selectedCount} of ${olderCount}`;
}

const SHORTCUT =
  'text-xs font-medium px-2 py-1 rounded text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 min-h-[32px] max-md:min-h-[44px]';

export default function BudgetComparePicker({
  older, selectedIds, max, atCap, onToggle, onLatestOnly, onShowAll,
}) {
  const reasonId = useId();
  if (!older || older.length === 0) return null;
  const selected = new Set(selectedIds.map(Number));
  const reason = capReason(max);

  return (
    <Dropdown
      align="right"
      haspopup="dialog"
      panelClassName="w-80 p-2"
      triggerClassName={TOOLBAR_DISCLOSURE_TRIGGER}
      title="Choose which older budgets to show"
      ariaLabel="Choose older budgets to compare"
      triggerTestId="budget-compare-picker"
      label={compareTriggerLabel(selected.size, older.length)}
    >
      <div data-testid="budget-compare-panel">
        <div className="flex items-center justify-between gap-2 px-1 pb-1.5 mb-1 border-b border-gray-100 dark:border-gray-700">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Older budgets</span>
          <span className="flex gap-1">
            <button type="button" className={SHORTCUT} onClick={onLatestOnly} data-testid="budget-compare-latest-only">
              Latest only
            </button>
            <button
              type="button"
              className={SHORTCUT}
              onClick={onShowAll}
              title={older.length > max ? `Shows the newest ${max}` : 'Shows every older budget'}
              data-testid="budget-compare-show-all"
            >
              Show all
            </button>
          </span>
        </div>
        <p className="md:hidden px-1 pb-1 text-xs text-gray-500 dark:text-gray-400" data-testid="budget-compare-phone-note">
          Applies to the printed report.
        </p>
        <ul className="space-y-0.5">
          {older.map((v) => {
            const id = Number(v.id);
            const checked = selected.has(id);
            const disabled = !checked && atCap;
            return (
              <li key={id}>
                <label
                  className={`flex items-center gap-2 px-1.5 py-1 rounded min-h-[32px] max-md:min-h-[44px] ${disabled ? 'cursor-not-allowed text-gray-400 dark:text-gray-500' : 'cursor-pointer text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                  title={disabled ? reason : undefined}
                  data-testid={`budget-compare-row-${id}`}
                >
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                    checked={checked}
                    disabled={disabled}
                    aria-describedby={disabled ? reasonId : undefined}
                    onChange={() => onToggle(id)}
                    data-testid={`budget-compare-check-${id}`}
                  />
                  <span className="flex-1 min-w-0 truncate text-sm">{v.version_name}</span>
                  {v.is_baseline ? (
                    <span
                      className="flex-shrink-0 text-[11px] font-medium px-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
                      title={BASELINE_TITLE}
                      data-testid={`budget-compare-baseline-${id}`}
                    >
                      Baseline
                    </span>
                  ) : null}
                  <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                    {versionDate(v.created_at)}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
        {atCap && older.length > max && (
          <p id={reasonId} className="mt-1.5 px-1 text-xs text-amber-700 dark:text-amber-400" data-testid="budget-compare-cap-reason">
            {reason}
          </p>
        )}
      </div>
    </Dropdown>
  );
}
